import React, { useEffect, useState } from "react";
import { Input, Modal, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { UiButton } from "../../ui";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  NameCell,
  StatusPill,
  DimCell,
  NumCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
  CellRow,
  TypeTag,
  BedTag,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { useBucketCounts } from "../common/bucketCounts";
import {
  approvePharmacyReturnItemApi,
  rejectPharmacyReturnItemApi,
  getPharmacyReturnsListApi,
  getPharmacyReturnApi,
} from "../../../services/pharmacyReturns.services";
import ReturnDetailModal from "./ReturnDetailModal";
import { formatAmount } from "../common/money";
import { getErrorMessage } from "../../../helpers/error";


/*
 * The three states a sales return can be in. "requested" is what the API calls
 * a return still waiting on somebody, which reads as Pending on screen.
 */
/* The bill's own sale types, so a return reads the same as the bill it came
   off. */
const SALE_TYPE_LABEL = {
  IP: "IP",
  OP: "OP",
  PATIENT: "Patient",
  COUNTER: "Counter Sale",
};

const BUCKETS = [
  { key: "requested", label: "Pending", color: "#EAAA5E" },
  { key: "approved", label: "Approved", color: "#12B76A" },
  { key: "rejected", label: "Rejected", color: "#F97066" },
];

const STATUS_TONE = {
  requested: ["#FFFAEB", "#B54708"],
  partially_approved: ["#FFFAEB", "#B54708"],
  approved: ["#ECFDF3", "#027A48"],
  rejected: ["#FEF3F2", "#B42318"],
};

const STATUS_LABEL = {
  requested: "Pending",
  partially_approved: "Partially Approved",
  approved: "Approved",
  rejected: "Rejected",
};


/** The medicines on a return - the first one named, the rest counted. */
const medicineSummary = (items = []) => {
  const names = items
    .map((i) => i.pharmacy_medicine_name || i.medicine_name)
    .filter(Boolean);
  if (!names.length) return "-";
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

const totalQty = (items = []) =>
  items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);

const Returns = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [approving, setApproving] = useState(null);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  /* The return being turned down, and the reason being typed for it. */
  const [rejecting, setRejecting] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [rejectWorking, setRejectWorking] = useState(false);

  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    setLoading(true); 
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (status) queryParams.status = status;
    if (search) queryParams.search = search;
    getPharmacyReturnsListApi(new URLSearchParams(queryParams))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, status, refresh]);

  const counts = useBucketCounts({
    buckets: BUCKETS,
    response: data,
    activeKey: status,
    search,
  });


  /*
   * Approving from the row.
   *
   * A return is approved item by item - that is the only endpoint there is, and
   * it is the right shape, because a return with three medicines on it can be
   * settled three different ways. Approving from the row means approving all of
   * them, so it says how many before it does, and anything finer is a job for
   * the return itself.
   */
  const askToApprove = (record) => {
    const items = record.items || [];
    if (!items.length) {
      openDetail(record.id);
      return;
    }

    Modal.confirm({
      title: `Approve ${record.return_number || "this return"}?`,
      content:
        items.length === 1
          ? `${formatAmount(record.total_amount)} goes back and the stock returns to the shelf. This cannot be undone.`
          : `All ${items.length} medicines on it are approved, ${formatAmount(record.total_amount)} goes back and the stock returns to the shelf. To approve only some of them, open the return instead.`,
      okText: items.length === 1 ? "Approve" : `Approve all ${items.length}`,
      cancelText: "Not now",
      onOk: async () => {
        setApproving(record.id);
        try {
          await Promise.all(
            items.map((item) => approvePharmacyReturnItemApi(record.id, item.id))
          );
          message.success(`${record.return_number || "Return"} approved, stock updated`);
          refreshPage();
        } catch (err) {
          message.error(
            err?.response?.data?.message || "Failed to approve this return"
          );
        } finally {
          setApproving(null);
        }
      },
    });
  };

  /*
   * Turning a return down from the row.
   *
   * The mirror of approving from the row, and item by item for the same
   * reason - that is the only endpoint there is. A reason is required, as it
   * is on a single item: a rejected return is a refusal somebody has to be
   * able to explain afterwards, and the remark is the only place it is
   * written down. Anything finer than all-or-nothing is a job for the return
   * itself, where each item can be settled its own way.
   */
  const askToReject = (record) => {
    const items = record.items || [];
    if (!items.length) {
      openDetail(record.id);
      return;
    }
    setRejecting(record);
    setRejectRemarks("");
  };

  const confirmReject = () => {
    const remarks = rejectRemarks.trim();
    if (!remarks) {
      message.warning("Please enter a reason for rejection");
      return;
    }
    const record = rejecting;
    const items = record?.items || [];
    setRejectWorking(true);
    Promise.all(
      items.map((item) =>
        rejectPharmacyReturnItemApi(record.id, item.id, { review_remarks: remarks })
      )
    )
      .then(() => {
        message.success(`${record.return_number || "Return"} rejected`);
        setRejecting(null);
        refreshPage();
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setRejectWorking(false));
  };

  const openDetail = (returnId) => {
    getPharmacyReturnApi(returnId).then((res) => {
      setSelectedReturn(res.data);
      setDetailOpen(true);
    });
  };

  const columns = [
    {
      title: "Return",
      dataIndex: "return_number",
      key: "return_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    {
      title: "Against Bill",
      dataIndex: "bill_number",
      key: "bill_number",
      render: (value) => <RefCell dim>{value}</RefCell>,
    },
    {
      title: "Patient",
      key: "patient_name",
      render: (_, record) => <NameCell name={record.patient_name} />,
    },
    {
      /*
       * How the return settles depends on where the sale came from - an IP
       * return credits the admission, a counter return is cash back across the
       * counter - so the row says which.
       */
      title: "Type",
      key: "sale_type",
      render: (_, record) => {
        const type = record.sale_type ?? record.bill_sale_type;
        if (!type) return <DimCell>—</DimCell>;
        return (
          <CellRow>
            <TypeTag type={type}>{SALE_TYPE_LABEL[type] || type}</TypeTag>
            {record.bed_no ? <BedTag>{record.bed_no}</BedTag> : null}
          </CellRow>
        );
      },
    },
    {
      title: "Medicine",
      key: "medicine",
      render: (_, record) => medicineSummary(record.items),
    },
    {
      title: "Qty",
      key: "qty",
      align: "right",
      render: (_, record) => <NumCell>{totalQty(record.items)}</NumCell>,
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (value) => (
        <NumCell>
          <b>{formatAmount(value)}</b>
        </NumCell>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <StatusPill tone={STATUS_TONE[value] || STATUS_TONE.rejected}>
          {STATUS_LABEL[value] || value}
        </StatusPill>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 128,
      render: (_, record) => {
        /* Only a return still waiting on a decision can be given one. */
        const awaiting = record.status === "requested" || record.status === "pending";
        return (
          <RowActions>
            {awaiting ? (
              <UiButton
                size="sm"
                disabled={approving === record.id}
                onClick={() => askToApprove(record)}
              >
                {approving === record.id ? "Approving…" : "Approve"}
              </UiButton>
            ) : null}
            {/* The other half of the decision. A cross rather than a second
                button: the two outcomes are not equally likely, and a row
                carrying two full-width buttons and a mark had nowhere left
                to put the figures. */}
            {awaiting ? (
              <IconAction
                icon="close"
                tone="danger"
                label="Reject this return"
                disabled={approving === record.id}
                onClick={() => askToReject(record)}
              />
            ) : null}
            <IconAction
              icon="billgo"
              label="View return"
              onClick={() =>
                record.bill
                  ? navigate(`/pharmacy/billing/${record.bill}`)
                  : openDetail(record.id)
              }
            />
          </RowActions>
        );
      },
    },
  ];

  const total = Number(data?.count) || 0;

  const segments = BUCKETS.map((b) => ({ ...b, count: counts[b.key] ?? 0 }));
  /* The whole list, which the unfiltered response knows outright; until one
     has come back the segments in hand are the best there is. */
  const bandTotal =
    counts.ALL ?? segments.reduce((sum, s) => sum + s.count, 0);

  const selectStatus = (key) => {
    setPage(1);
    setStatus((prev) => (prev === key ? null : key));
  };

  return (
    <ListScreen>
      <ListHead title="Returns" />

      <DistBand
        total={bandTotal}
        label="Sales Returns"
        segments={segments}
        activeKey={status}
        onSelect={selectStatus}
      />

      <ListCard
        title="Sales Returns"
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: "Search return, bill or medicine",
        }}
        onClear={
          search || status
            ? () => {
                setSearch("");
                setStatus(null);
                setPage(1);
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={data?.results || []}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>

      {/*
        * Why it was turned down, which the server stores against every item
        * and the detail screen prints back under its status.
        */}
      <Modal
        title={`Reject ${rejecting?.return_number || "this return"}?`}
        open={Boolean(rejecting)}
        onCancel={() => setRejecting(null)}
        centered
        width={420}
        okText={
          (rejecting?.items || []).length > 1
            ? `Reject all ${(rejecting?.items || []).length}`
            : "Reject"
        }
        okButtonProps={{ danger: true, loading: rejectWorking }}
        cancelText="Not now"
        onOk={confirmReject}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {(rejecting?.items || []).length > 1
            ? `All ${(rejecting?.items || []).length} medicines on it are turned down, nothing goes back to stock and no money is refunded. To settle only some of them, open the return instead.`
            : "Nothing goes back to stock and no money is refunded."}
        </Typography.Paragraph>
        <Typography.Text>Reason for rejection</Typography.Text>
        <Input.TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="e.g. Item was already opened / not eligible for return"
        />
      </Modal>

      {detailOpen && (
        <ReturnDetailModal
          open={detailOpen}
          setOpen={setDetailOpen}
          returnRequest={selectedReturn}
          refreshPage={() => {
            openDetail(selectedReturn.id);
            refreshPage();
          }}
        />
      )}
    </ListScreen>
  );
};

export default Returns;
